/** 手机布局：工具栏允许换行，手写弹窗限制动态可视高度，避免窄屏和键盘弹出后遮住业务操作。 */
import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { AlertTriangle, CheckCircle2, Loader2, RotateCcw, Undo2, UserPlus, Waves, SlidersHorizontal } from "lucide-react";

const ACTION = {
  recycle: { zh: "退回公海", en: "Return to pool", Icon: Waves, cls: "bg-sky-600 hover:bg-sky-700" },
  assign: { zh: "分配给销售", en: "Assign to sales", Icon: UserPlus, cls: "bg-brand-500 hover:bg-brand-600" },
  stage: { zh: "调整阶段", en: "Adjust stage", Icon: SlidersHorizontal, cls: "bg-indigo-600 hover:bg-indigo-700" },
};
const STAGE_OPTS = [
  { k: "new", zh: "新线索", en: "New" }, { k: "site_eval", zh: "现场评估", en: "Site eval" }, { k: "proposal", zh: "方案", en: "Proposal" },
  { k: "estimate", zh: "报价", en: "Estimate" }, { k: "contract", zh: "合同", en: "Contract" }, { k: "signed", zh: "已签约", en: "Signed" }, { k: "nurturing", zh: "培育", en: "Nurturing" },
];
const todayStr = () => new Date().toISOString().slice(0, 10);

/* 批量操作确认：预检（总数 / 将处理 / 跳过原因 + 样本）→ 备注 → 执行 → 汇总。target = { ids } 或 { filter }。 */
export const BulkActionDialog = ({ api, zh, open, onClose, action, target, sales = [], initialSales = "", onDone }) => {
  const [prev, setPrev] = useState(null);
  const [err, setErr] = useState(null);
  const [reason, setReason] = useState("");
  const [salesName, setSalesName] = useState("");
  const [stage, setStage] = useState("site_eval");
  const [signedDate, setSignedDate] = useState(todayStr());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [tgt, setTgt] = useState(null);
  const [previewTick, setPreviewTick] = useState(0);
  const submitting = useRef(false);
  const A = ACTION[action] || ACTION.recycle;
  const params = action === "assign" ? { salesName: salesName || sales[0] || "" } : action === "stage" ? { stage, signedDate: stage === "signed" ? signedDate : undefined } : {};
  useEffect(() => {
    if (!open) { setTgt(null); return; }
    setPrev(null); setErr(null); setResult(null); setReason(""); setBusy(false);
    submitting.current = false;
    if (action === "assign") setSalesName(initialSales || sales[0] || "");
    setTgt(target);
  }, [open, action]); // eslint-disable-line
  useEffect(() => {
    if (!open || !tgt || result) return;
    // 切换销售/重新打开时旧预检可能更晚返回，不能把旧人数当成当前销售的确认结果。
    let active = true;
    setPrev(null); setErr(null);
    api.post("/crm/leads/bulk", { action, ...tgt, preview: true, params })
      .then((r) => { if (active) setPrev(r.data); }).catch((e) => { if (active) setErr(e?.detailText || e.message); });
    return () => { active = false; };
  }, [open, action, tgt, salesName, stage, signedDate, previewTick]); // eslint-disable-line
  useEffect(() => {
    if (open && action === "assign" && !salesName && (initialSales || sales[0])) setSalesName(initialSales || sales[0]);
  }, [open, action, sales, salesName, initialSales]);
  const reasonRequired = action === "stage" && reason.trim().length < 2;
  const run = async () => {
    // 手机连点/网络较慢时仍只提交一次；提交中不允许关闭或更换目标。
    if (submitting.current || !prev?.willProcess) return;
    submitting.current = true;
    setBusy(true);
    try {
      const { data } = await api.post("/crm/leads/bulk", { action, ...tgt, reason, params });
      setResult(data); toast.success(zh ? `${A.zh}完成 · ${data.done} 条` : `${A.en} done · ${data.done}`); onDone?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false); submitting.current = false;
  };
  const Skip = ({ k, label }) => {
    const s = prev?.skipped?.[k]; if (!s || !s.count) return null;
    return (
      <div className="rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2 text-[11px] text-amber-800" data-testid={`bulk-skip-${k}`}>
        <p className="font-semibold flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{label} · {s.count}</p>
        <p className="text-amber-700/80 mt-0.5 truncate">{s.sample.map((x) => x.clientName || x.id).join("、")}{s.count > s.sample.length ? " …" : ""}</p>
      </div>
    );
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting.current) onClose(); }}>
      <DialogContent className="max-w-md" closeDisabled={busy} data-testid="bulk-action-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm"><A.Icon className="h-4 w-4 text-brand-500" />{zh ? `批量${A.zh}` : `Bulk · ${A.en}`}</DialogTitle>
          <DialogDescription className="text-xs">{(tgt || target)?.ids ? (zh ? `已勾选 ${(tgt || target).ids.length} 条客户` : `${(tgt || target).ids.length} selected`) : (zh ? "当前筛选条件下的全部客户，数量以本次核对为准。" : "All matching leads. The counts below reflect the current check.")}</DialogDescription>
        </DialogHeader>
        {action === "assign" && !result && (
          <label className="block text-sm">{zh ? "分配给" : "Assign to"}
            <select data-testid="bulk-sales-select" disabled={busy} value={salesName} onChange={(e) => setSalesName(e.target.value)} className="mt-1 w-full min-h-11 rounded-lg border border-slate-200 px-3 bg-white">
              {!sales.length && <option value="">{zh ? "暂无可用销售" : "No active salespeople"}</option>}
              {sales.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        )}
        {tgt?.poolOnly && !result && <p className="text-xs text-slate-500">{zh ? "仅分配仍在公海的客户。已被领取、已签约或已转项目的客户会自动跳过，执行时按最新状态核验。" : "Only leads still in the pool are assigned. Claimed, signed or converted leads are skipped after a final status check."}</p>}
        {err && <div className="text-sm text-red-600" role="alert" data-testid="bulk-error"><p>{err}</p><Button variant="outline" className="mt-2 min-h-11" data-testid="bulk-preview-retry" onClick={() => setPreviewTick(v => v + 1)}>{zh ? "重新检查" : "Retry check"}</Button></div>}
        {!prev && !err && <p className="text-xs text-slate-400 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />{zh ? "预检中…" : "Checking…"}</p>}
        {prev && !result && (
          <div className="space-y-3 text-xs">
            {action === "stage" && (
              <div className="space-y-2" data-testid="bulk-stage-fields">
                <label className="block">{zh ? "调整到阶段" : "Target stage"}
                  <select data-testid="bulk-stage-select" value={stage} onChange={(e) => setStage(e.target.value)} className="mt-0.5 w-full h-8 rounded-md border border-slate-200 px-2 text-xs bg-white">
                    {STAGE_OPTS.map((s) => <option key={s.k} value={s.k}>{zh ? s.zh : s.en}</option>)}
                  </select>
                </label>
                {stage === "signed" && (
                  <label className="block">{zh ? "合同签订日期（统一，不晚于今天）" : "Contract signed date"}
                    <input type="date" data-testid="bulk-stage-signed-date" value={signedDate} max={todayStr()} onChange={(e) => setSignedDate(e.target.value)} className="mt-0.5 w-full h-8 rounded-md border border-slate-200 px-2 text-xs bg-white" />
                  </label>
                )}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-slate-50 border border-slate-200 py-2"><p className="text-[10px] text-slate-500">{zh ? "匹配" : "Matched"}</p><p className="text-lg font-bold text-slate-800" data-testid="bulk-total">{prev.total}</p></div>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 py-2"><p className="text-[10px] text-emerald-700">{zh ? "将处理" : "Will process"}</p><p className="text-lg font-bold text-emerald-700" data-testid="bulk-will">{prev.willProcess}</p></div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 py-2"><p className="text-[10px] text-amber-700">{zh ? "跳过" : "Skipped"}</p><p className="text-lg font-bold text-amber-700" data-testid="bulk-skipped">{Object.values(prev.skipped || {}).reduce((a, s) => a + (s.count || 0), 0)}</p></div>
            </div>
            <Skip k="protected" label={action === "stage" ? (zh ? "已签约客户请在详情页单独调整，跳过" : "Signed — adjust individually, skipped") : (zh ? "已签约 / 已转项目，自动跳过" : "Signed / converted — skipped")} />
            <Skip k="already" label={action === "recycle" ? (zh ? "已在公海，跳过" : "Already in pool — skipped") : action === "stage" ? (zh ? "已处于目标阶段，跳过" : "Already at target stage — skipped") : (zh ? "已归属该销售，跳过" : "Already assigned — skipped")} />
            <Skip k="notInPool" label={zh ? "已不在公海，跳过" : "No longer in the pool — skipped"} />
            {prev.sample?.length > 0 && <p className="text-[11px] text-slate-500 truncate">{zh ? "例如：" : "e.g. "}{prev.sample.map((x) => x.clientName || x.id).join("、")}{prev.willProcess > prev.sample.length ? " …" : ""}</p>}
            <label className="block">{action === "stage" ? (zh ? "统一调整原因（必填，写入每条客户的阶段记录并通知负责销售）" : "Reason (required, logged per lead)") : (zh ? "备注原因（写入每条客户的分配历史）" : "Reason (recorded on each lead)")}
              <input data-testid="bulk-reason" disabled={busy} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={zh ? (action === "recycle" ? "如：历史导入数据统一退回公海" : action === "stage" ? "如：历史数据补录 / 阶段录入错误统一纠正" : "如：区域调整") : action === "stage" ? "Required" : "Optional"} className={`mt-0.5 w-full min-h-11 rounded-md border px-2 text-xs ${reasonRequired ? "border-amber-300" : "border-slate-200"}`} />
            </label>
            <div className="flex-wrap md:flex-nowrap flex items-center justify-between pt-1">
              <span className="text-[10px] text-slate-400 inline-flex items-center gap-1"><Undo2 className="h-3 w-3" />{zh ? "执行后 30 分钟内可一键撤销" : "Undo available for 30 min"}</span>
              <div className="flex-wrap md:flex-nowrap flex gap-2">
                <Button variant="outline" size="sm" className="min-h-11" disabled={busy} onClick={onClose} data-testid="bulk-cancel">{zh ? "取消" : "Cancel"}</Button>
                <Button size="sm" onClick={run} disabled={busy || !prev.willProcess || (action === "assign" && !salesName) || reasonRequired} className={`${A.cls} min-h-11 text-white`} data-testid="bulk-confirm">{busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <A.Icon className="h-3.5 w-3.5 mr-1" />}{zh ? `确认${A.zh} ${prev.willProcess} 条` : `${A.en} ${prev.willProcess}`}</Button>
              </div>
            </div>
          </div>
        )}
        {result && (
          <div className="space-y-3 text-xs" data-testid="bulk-result">
            <p className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="h-4 w-4" />{zh ? `已${A.zh} ${result.done} 条` : `${A.en}: ${result.done}`}<span className="text-slate-400 font-normal">· {zh ? "跳过" : "skipped"} {Object.values(result.skipped || {}).reduce((a, s) => a + (s.count || 0), 0)}</span></p>
            {result.syncPending && <p className="text-amber-700">{zh ? "分配已保存，关联电销或推荐记录同步失败，请联系管理员核对，无需重复分配。" : "Assignments saved. Linked records could not sync; contact an administrator without assigning again."}</p>}
            {result.failed?.length > 0 && (
              <div className="rounded-md bg-rose-50 border border-rose-200 px-2.5 py-2 text-[11px] text-rose-700" data-testid="bulk-failed">
                <p className="font-semibold">{zh ? `未能处理 ${result.failed.length} 条` : `${result.failed.length} failed`}</p>
                {result.failed.slice(0, 5).map((f) => <p key={f.id} className="truncate">{f.clientName || f.id}：{f.detail}</p>)}
              </div>
            )}
            <p className="text-[11px] text-slate-500 inline-flex items-center gap-1"><RotateCcw className="h-3 w-3" />{zh ? "如需撤销，请使用筛选栏的「撤销上一次批量操作」（30 分钟内）。" : "Use “Undo last bulk action” in the toolbar within 30 minutes."}</p>
            <div className="flex justify-end"><Button size="sm" onClick={onClose} data-testid="bulk-done">{zh ? "完成" : "Done"}</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* 「撤销上一次批量操作」按钮：读取最近一次可撤销操作，显示剩余分钟。 */
export const BulkUndoButton = ({ api, zh, refreshKey, onUndone }) => {
  const [op, setOp] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/crm/leads/bulk/recent").then((r) => setOp((r.data || []).find((o) => o.canUndo) || null)).catch(() => setOp(null)); }, [api, refreshKey]);
  if (!op) return null;
  const mins = Math.max(0, Math.round((new Date(op.undoUntil) - Date.now()) / 60000));
  const label = op.label || op.action;
  const undo = async () => {
    if (!window.confirm(zh ? `撤销「${label} ${op.count} 条」？仅恢复此后未被单独改动的客户。` : `Undo "${label} × ${op.count}"?`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/crm/leads/bulk/${op.id}/undo`); toast.success(zh ? `已撤销：恢复 ${data.restored} 条，跳过 ${data.skipped} 条` : `Undone: ${data.restored} restored, ${data.skipped} skipped`); setOp(null); onUndone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Button data-testid="crm-bulk-undo" variant="outline" onClick={undo} disabled={busy} className="h-9 border-amber-200 text-amber-700 hover:bg-amber-50" title={`${op.by} · ${op.at?.slice(0, 16)}`}>
      {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Undo2 className="h-4 w-4 mr-1" />}{zh ? `撤销上一次批量（${label} ${op.count} 条 · 剩 ${mins} 分）` : `Undo last bulk (${label} ×${op.count} · ${mins}m)`}
    </Button>
  );
};
